import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      return (savedTheme as ThemeMode) || 'light';
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return 'light';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('theme', themeMode);
    } catch (error) {
      console.error('Error setting theme in localStorage:', error);
    }
  }, [themeMode]);

  const toggleTheme = useMemo(
    () => () => {
      setThemeMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
    },
    [],
  );

  const value = useMemo(
    () => ({
      themeMode,
      toggleTheme,
    }),
    [themeMode, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
